import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import api from '../../api/api';

const getPaymentRequestCode = (mode) => {
  const payType = (mode.PayType || '').toLowerCase();
  const prefix = payType === 'gpay' ? 'G' : payType === 'upi' ? 'U' : payType === 'bank' ? 'B' : (mode.PayType || '').charAt(0).toUpperCase();
  return prefix && mode.Bid ? `${prefix}${mode.Bid}` : '';
};

const BankPaymentRequestModal = ({ open, onClose, userName, requestModes, selectedRequestModes, onRequestModeChange }) => {
  const [paymentList, setPaymentList] = useState([]);
  const [loadingModes, setLoadingModes] = useState(false);
  const [modeError, setModeError] = useState('');

  useEffect(() => {
    if (!open) return;

    if (requestModes?.length) {
      setPaymentList(requestModes);
      setLoadingModes(false);
      setModeError('');
      return;
    }

    let mounted = true;
    const fetchPaymentModes = async () => {
      setLoadingModes(true);
      setModeError('');
      try {
        const res = await api.GetBankPaymentModeListRev(userName);
        const rows = res?.Table ?? res?.GetBankPaymentModeListRevjs?.Table ?? [];
        if (mounted) setPaymentList(rows);
      } catch (err) {
        console.error('Failed to fetch bank payment modes', err);
        if (mounted) setModeError('Failed to fetch payment modes.');
      } finally {
        if (mounted) setLoadingModes(false);
      }
    };

    fetchPaymentModes();
    return () => {
      mounted = false;
    };
  }, [open, userName, requestModes]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Request Payment Mode</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl leading-none">
            x
          </button>
        </div>

        {modeError && <p className="mb-3 text-sm text-red-600">{modeError}</p>}

        <div className="max-h-80 overflow-y-auto space-y-2">
          {loadingModes ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : paymentList.length ? (
            paymentList.map((mode, index) => {
              const key = `${mode.Bid}-${mode.PayType}-${mode.PayName}-${index}`;
              const requestCode = getPaymentRequestCode(mode);
              const checked = selectedRequestModes.some((selectedMode) => getPaymentRequestCode(selectedMode) === requestCode);
              return (
                <label key={key} className="flex items-center gap-3 p-3 border rounded-lg text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onRequestModeChange(mode, e.target.checked)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                  <span>{mode.DisplayName || `${mode.HolderName || ''} - ${mode.PayType || ''} (${mode.PayName || ''})`}</span>
                </label>
              );
            })
          ) : (
            <p className="text-sm text-gray-500">No payment modes found.</p>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button onClick={onClose} className="bg-gray-500 hover:bg-gray-600 px-3 py-1">
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
};

const SalesOrderHeader = ({
  orderDetails,
  user,
  setSuccess,
  setError,
  setOrderDetails,
}) => {
  const [editableAddress, setEditableAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [amountPaid, setAmountPaid] = useState(0);
  const [payMode, setPayMode] = useState('');
  const [refNo, setRefNo] = useState('');
  const [paymentModes, setPaymentModes] = useState([]);
  const [salesTypes, setSalesTypes] = useState([]);
  const [selectedSalesType, setSelectedSalesType] = useState({
    code: '',
    name: '',
  });
  const [orderDate, setOrderDate] = useState(''); 
  const [deliveryDate, setDeliveryDate] = useState(''); 
  const [openBankRequestModal, setOpenBankRequestModal] = useState(false);
  const [selectedPaymentRequests, setSelectedPaymentRequests] = useState([]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    if (orderDetails) {
      setEditableAddress(orderDetails.Address || '');
      setAmountPaid(orderDetails.amountPaid ?? 0);
      setRefNo(orderDetails.Refno ?? '');
      setPayMode(orderDetails.Paymode ?? '');
      setPaymentModes(orderDetails.Table2 || []);
      setSalesTypes(orderDetails.Table3 || []);
      setOrderDate(formatDate(orderDetails.Podate));
      setDeliveryDate(formatDate(orderDetails.DeliveryDate)); // ✅ correctly bound

      const reqtoCodes = String(orderDetails.Reqto || '').split('/').filter(Boolean);
      const requestModes = orderDetails.Table4 || [];
      setSelectedPaymentRequests(
        requestModes.filter((mode) => reqtoCodes.includes(getPaymentRequestCode(mode)))
      );

      if (orderDetails.SubTypeCode && orderDetails.SubTypeName) {
        setSelectedSalesType({
          code: orderDetails.SubTypeCode,
          name: orderDetails.SubTypeName,
        });
      }
    }
  }, [orderDetails]);

  const handleRequestModeChange = (mode, checked) => {
    const requestCode = getPaymentRequestCode(mode);
    setSelectedPaymentRequests((prev) => {
      if (checked) {
        const exists = prev.some((selectedMode) => getPaymentRequestCode(selectedMode) === requestCode);
        return exists ? prev : [...prev, mode];
      }
      return prev.filter((selectedMode) => getPaymentRequestCode(selectedMode) !== requestCode);
    });
  };

  const handleUpdateOrder = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const reqTo = selectedPaymentRequests.map(getPaymentRequestCode).filter(Boolean).join('/');
      const payload = {
        Poid: orderDetails?.Poid,
        Address: editableAddress,
        MobileNo: orderDetails?.MobileNo,
        User: user?.UsrName,
        amountPaid: amountPaid,
        Paymode: payMode,
        Refno: refNo,
        Deliverydate: deliveryDate,
        SubTypecode: orderDetails?.SubTypeCode || selectedSalesType.code,
        subtypename: orderDetails?.SubTypeName || selectedSalesType.name,
        Reqto: reqTo,
      };

      console.log("🔹 Update Payload:", payload);

      await api.updateSalesOrderDetails(payload);

      if (setOrderDetails) {
        setOrderDetails((prev) => ({
          ...prev,
          Address: editableAddress,
          amountPaid,
          Paymode: payMode,
          Refno: refNo,
          Podate: orderDate,
          DeliveryDate: deliveryDate,
          SubTypeCode: payload.SubTypecode,
          SubTypeName: payload.subtypename,
          Reqto: payload.Reqto,
        }));
      }

      setSuccess('Order details updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error("❌ Update Failed:", err);
      setError('Failed to update order.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-white shadow-lg rounded-xl overflow-hidden">
      <h2 className="text-lg sm:text-xl font-bold mb-4 text-center">
        Customer & Sales Order Info
      </h2>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Customer Name */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Customer Name
            </label>
            <input
              type="text"
              value={orderDetails?.Vname || ''}
              readOnly
              className="w-full p-2 border rounded-lg bg-gray-100 text-gray-700"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Address*
            </label>
            <textarea
              value={editableAddress}
              onChange={(e) => setEditableAddress(e.target.value)}
              rows="3"
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Amount Paid */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Amount Paid
            </label>
            <input
              type="number"
              value={amountPaid}
              onChange={(e) => setAmountPaid(Number(e.target.value))}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Reference No */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Reference No
            </label>
            <input
              type="text"
              value={refNo}
              onChange={(e) => setRefNo(e.target.value)}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Mobile */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Mobile
            </label>
            <input
              type="text"
              value={orderDetails?.MobileNo || ''}
              readOnly
              className="w-full p-2 border rounded-lg bg-gray-100 text-gray-700"
            />
          </div>

          {/* Balance */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Balance
            </label>
            <input
              type="text"
              value={(orderDetails?.Balance || 0).toLocaleString('en-IN', {
                style: 'currency',
                currency: 'INR',
              })}
              readOnly
              className="w-full p-2 border rounded-lg bg-gray-100 text-gray-700"
            />
          </div>

          {/* Delivery Date */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Delivery Date
            </label>
            <input
              type="date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Pay Mode */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Pay Mode
            </label>
            <select
              value={payMode}
              onChange={(e) => setPayMode(e.target.value)}
              // disabled={!!orderDetails?.Paymode}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select</option>
              {paymentModes.map((mode, index) => (
                <option key={mode.bANKID ?? index} value={mode.PAYMODE || mode.Mode}>
                  {mode.PAYMODE || mode.Mode}
                </option>
              ))}
            </select>
          </div>

          {/* Sales Type */}
<div>
  <label className="block mb-2 text-sm font-bold text-gray-700">
    Sales Type
  </label>
  <div className="flex items-center gap-3">
  <select
    value={orderDetails.SubTypeCode || ''}
    onChange={(e) => {
      const selected = orderDetails.Table3.find(
        (st) => st.TypeStr === e.target.value
      );
      setOrderDetails({
        ...orderDetails,
        SubTypeCode: selected?.TypeStr || '',
        SubTypeName: selected?.TypeName || '',
      });
    }}
    className="w-full p-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    disabled={!!orderDetails?.Cpid} // ✅ Disable dropdown if Cpid exists
  >
    <option value="">Select Sales Type</option>
    {orderDetails.Table3?.map((st, idx) => (
      <option key={idx} value={st.TypeStr}>
        {st.TypeName}
      </option>
    ))}
  </select>
  <button
    type="button"
    onClick={() => setOpenBankRequestModal(true)}
    className="shrink-0 px-4 py-3 text-sm font-bold text-blue-700 bg-blue-50 border border-blue-300 rounded-lg shadow-sm hover:bg-blue-100 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
  >
    Request to
  </button>
  </div>
  <input
    type="hidden"
    name="SubTypeCode"
    value={orderDetails.SubTypeCode || ''}
  />
</div>

        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 mt-6">
        <Button
          type="button"
          onClick={handleUpdateOrder}
          disabled={loading}
          className="px-4 py-2"
        >
          {loading ? 'Updating...' : 'Update Sales Order Details'}
        </Button>
      </div>
      <BankPaymentRequestModal
        open={openBankRequestModal}
        onClose={() => setOpenBankRequestModal(false)}
        userName={user?.UsrName}
        requestModes={orderDetails?.Table4 || []}
        selectedRequestModes={selectedPaymentRequests}
        onRequestModeChange={handleRequestModeChange}
      />
    </div>
  );
};

export default SalesOrderHeader;




